import { LendAPIPayload } from './lendAPIPrequalHelper';
import { Prequal, PrequalApplicationStatus, FullApplicationStatus, LoanPurpose } from '../schema';
import { formatEpochDate } from './prequalHelper';
import { DEFAULT_PREQUAL_APR_DISCLAIMER, LEAD_SOURCE } from '../constants';

export interface IterableEventParams {
  email: string;
  eventName: string;
  eventData: {
    [key: string]: any;
  };
}

export interface CombinedAppPreOfferLoginParams {
  email: string;
  firstName: string;
  lastName: string;
  sessionId: string;
  resumeLink: string;
  customParams?: string;
}

export interface BoatInfo {
  boatMake?: string;
  boatModel?: string;
  boatImtId?: string;
  sellerName?: string;
  sellerEmail?: string;
  sellerPartyId?: string;
  sellerType?: string;
  source?: string;
}

/**
 * Maps source code from URL parameters to proper display name
 */
function mapSourceToDisplayName(source?: string): string {
  if (!source) {
    return 'Unknown';
  }

  const sourceCode = parseInt(source, 10);
  if (Number.isNaN(sourceCode)) {
    return 'Unknown';
  }

  return LEAD_SOURCE.get(sourceCode) || 'Unknown';
}

/**
 * Maps LendAPI loan_type values to internal loanType values
 * LendAPI values: "new boat", "used boat", "refinance"
 * Internal values: "new", "used", "refinance"
 */
export function mapLendAPILoanType(loanType?: string): string | undefined {
  if (!loanType) {
    return undefined;
  }

  const normalized = loanType.toLowerCase();
  if (normalized === 'new boat') return 'new';
  if (normalized === 'used boat') return 'used';
  if (normalized === 'refinance') return 'refinance';

  return loanType;
}

/**
 * Extracts boat information from custom URL parameters
 */
export function extractBoatInfoFromCustomParams(customParams?: string): BoatInfo {
  if (!customParams) {
    return {};
  }

  try {
    const params = new URLSearchParams(decodeURIComponent(customParams));
    const boatInfoParam = params.get('boatInfo');
    const sourceParam = params.get('source');

    const result: BoatInfo = {};

    if (boatInfoParam) {
      const boatInfo = JSON.parse(decodeURIComponent(boatInfoParam));
      Object.assign(result, {
        boatMake: boatInfo.make,
        boatModel: boatInfo.model,
        boatImtId: boatInfo.imtId,
        sellerName: boatInfo.sellerName,
        sellerEmail: boatInfo.sellerEmail,
        sellerPartyId: boatInfo.partyId,
        sellerType: boatInfo.sellerType,
      });
    }

    if (sourceParam) {
      result.source = sourceParam;
    }

    return result;
  } catch (error) {
    console.error('[LendAPI] Error extracting boat info from custom params:', error);
    return {};
  }

  return {};
}

/**
 * Shared helper to build common event data for both PreQualStarted and PreQualCompleted events
 */
function buildBaseEventData(
  lendAPIPayload: LendAPIPayload,
  prequalData: Partial<Prequal>,
  applicationStatus: string,
  additionalFields: { [key: string]: any } = {}
): { [key: string]: any } {
  const customParams = lendAPIPayload.app_url_params?.custom || lendAPIPayload.custom;
  const boatInfo = extractBoatInfoFromCustomParams(customParams);

  const dataFields: { [key: string]: any } = {
    prequalId: prequalData.prequalApplicationId,
    firstName: prequalData.firstName,
    lastName: prequalData.lastName,
    applicationStatus,
    source: mapSourceToDisplayName(boatInfo.source),
    client: 'LendAPI',
    ...(prequalData.createdAt && {
      startedAt: formatEpochDate(prequalData.createdAt),
    }),
    ...additionalFields,
  };

  // Add boat info if available (only if user came from a boat specific page)
  if (Object.keys(boatInfo).length > 0) {
    // Extract source separately to apply mapping
    const { source, ...otherBoatInfo } = boatInfo;
    Object.assign(dataFields, otherBoatInfo);
    // Override with mapped source
    if (source) {
      dataFields.source = mapSourceToDisplayName(source);
    }
  }

  return dataFields;
}

/**
 * Maps LendAPI payload to PreQualStarted Iterable event format
 */
export function mapLendAPIToIterableStarted(
  lendAPIPayload: LendAPIPayload,
  prequalData: Partial<Prequal>
): IterableEventParams {
  const eventData = buildBaseEventData(
    lendAPIPayload,
    prequalData,
    PrequalApplicationStatus.started
  );

  return {
    email: prequalData.email!,
    eventName: 'PreQualStarted',
    eventData,
  };
}

/**
 * Maps LendAPI payload to PreQualCompleted Iterable event format
 */
export function mapLendAPIToIterableCompleted(
  lendAPIPayload: LendAPIPayload,
  prequalData: Partial<Prequal>
): IterableEventParams {
  const additionalFields = {
    prequalDisclaimer: DEFAULT_PREQUAL_APR_DISCLAIMER,
    ...(prequalData.completedAt && {
      completedAt: formatEpochDate(prequalData.completedAt),
    }),
  };

  const eventData = buildBaseEventData(
    lendAPIPayload,
    prequalData,
    PrequalApplicationStatus.submitted,
    additionalFields
  );

  return {
    email: prequalData.email!,
    eventName: 'PreQualCompleted',
    eventData,
  };
}

/**
 * Maps LendAPI Full App Profile payload to FullAppStarted Iterable event format
 */
export function mapLendAPIToFullAppStarted(
  lendAPIPayload: LendAPIPayload,
  applicationId: string,
  prequalId?: string,
  resumeLink?: string,
  prequalApprovalOdds?: number,
  prequalApprovalOutcome?: string,
  buyingStage?: string
): IterableEventParams {
  const customParams = lendAPIPayload.app_url_params?.custom || lendAPIPayload.custom;
  const boatInfo = extractBoatInfoFromCustomParams(customParams);

  const dataFields: { [key: string]: any } = {
    applicationId,
    email: lendAPIPayload.email,
    firstName: lendAPIPayload.first_name,
    lastName: lendAPIPayload.last_name,
    applicationStatus: FullApplicationStatus.started,
    startedAt: formatEpochDate(Date.now()),
    source: mapSourceToDisplayName(boatInfo.source),
    client: 'LendAPI',
    loanPurpose: LoanPurpose.boat,
    loanType: mapLendAPILoanType(lendAPIPayload.loan_type),
    ...(prequalId && { prequalId }),
    ...(resumeLink && { resumeLink }),
    ...(prequalApprovalOdds !== undefined && { prequalApprovalOdds }),
    ...(prequalApprovalOutcome !== undefined && { prequalApprovalOutcome }),
    ...(buyingStage !== undefined && { buyingStage }),
  };

  if (Object.keys(boatInfo).length > 0) {
    const { source, ...otherBoatInfo } = boatInfo;
    Object.assign(dataFields, otherBoatInfo);
    if (source) {
      dataFields.source = mapSourceToDisplayName(source);
    }
  }

  return {
    email: lendAPIPayload.email!,
    eventName: 'FullAppStarted',
    eventData: dataFields,
  };
}

/**
 * Maps LendAPI Full App Confirm payload to FullAppCompleted Iterable event format
 */
export function mapLendAPIToFullAppCompleted(
  lendAPIPayload: LendAPIPayload,
  applicationId: string,
  prequalId?: string
): IterableEventParams {
  const customParams = lendAPIPayload.app_url_params?.custom || lendAPIPayload.custom;
  const boatInfo = extractBoatInfoFromCustomParams(customParams);

  const dataFields: { [key: string]: any } = {
    applicationId,
    email: lendAPIPayload.email,
    firstName: lendAPIPayload.first_name,
    lastName: lendAPIPayload.last_name,
    applicationStatus: FullApplicationStatus.submitted,
    completedAt: formatEpochDate(Date.now()),
    source: mapSourceToDisplayName(boatInfo.source),
    client: 'LendAPI',
    loanPurpose: LoanPurpose.boat,
    loanType: mapLendAPILoanType(lendAPIPayload.loan_type),
    ...(prequalId && { prequalId }),
  };
  if (Object.keys(boatInfo).length > 0) {
    const { source, ...otherBoatInfo } = boatInfo;
    Object.assign(dataFields, otherBoatInfo);
    if (source) {
      dataFields.source = mapSourceToDisplayName(source);
    }
  }
  return {
    email: lendAPIPayload.email!,
    eventName: 'FullAppCompleted',
    eventData: dataFields,
  };
}

export function mapLendAPIToIterableCombinedAppPreOfferLogin(
  params: CombinedAppPreOfferLoginParams
): IterableEventParams {
  const boatInfo = extractBoatInfoFromCustomParams(params.customParams);

  const dataFields: { [key: string]: any } = {
    email: params.email,
    firstName: params.firstName,
    lastName: params.lastName,
    applicationId: params.sessionId,
    resumeLink: params.resumeLink,
    source: mapSourceToDisplayName(boatInfo.source),
    client: 'LendAPI',
    loanPurpose: LoanPurpose.boat,
    createdAt: formatEpochDate(Date.now()),
  };

  if (Object.keys(boatInfo).length > 0) {
    const { source, ...otherBoatInfo } = boatInfo;
    Object.assign(dataFields, otherBoatInfo);
    if (source) {
      dataFields.source = mapSourceToDisplayName(source);
    }
  }

  return {
    email: params.email,
    eventName: 'CombinedAppPreOfferLogin',
    eventData: dataFields,
  };
}
